// Searching libraries on this device, like messages (./search.ts): their names, and every
// file in them by path and tags, over their decrypted index, so what someone searches for
// never reaches the server.

import { fileName } from './libraryEntries'
import { openEntries, type EntryApi, type OpenEntry } from './libraryPlay'
import { fold } from './search'

export interface NamedLibrary {
  id: string
  name: string
  kind: 'files' | 'git'
  locked?: boolean
}

export interface FileHit {
  library: string
  id: string
  title: string
  /** Folder, artist, album: what tells it apart. */
  detail: string
  kind: 'audio' | 'video' | 'image' | 'file'
  /** The decrypted entry (to play it from the results). */
  entry: OpenEntry
}

const folderOf = (path: string) => path.slice(0, Math.max(0, path.length - fileName(path).length - 1))

/** Libraries and repositories whose name matches. */
export function searchLibraryNames<L extends NamedLibrary>(libraries: L[], query: string): L[] {
  const q = fold(query.trim())
  if (q.length < 2) return []
  return libraries.filter((l) => !l.locked && fold(l.name).includes(q))
}

/**
 * Files of an encrypted library matching across path, title, people and label; with
 * `all`, a query too short to search matches everything (a queue's "add" list).
 */
export function searchEntries(library: string, entries: Iterable<OpenEntry>, query: string, all = false): FileHit[] {
  const q = fold(query.trim())
  if (q.length < 2 && !all) return []
  const out: FileHit[] = []
  for (const e of entries) {
    const m = e.meta
    const t = m.tags ?? {}
    const text = [m.path, t.title, t.artist, t.album, t.label, ...(t.composers ?? []), ...(t.producers ?? [])].filter(Boolean).join(' ')
    if (q.length >= 2 && !fold(text).includes(q)) continue
    out.push({
      library,
      id: e.entry.id,
      title: t.title ?? fileName(m.path),
      detail: [folderOf(m.path), t.artist, t.album].filter(Boolean).join(' · '),
      kind: m.kind,
      entry: e,
    })
  }
  return out
}

/** Everything searchable in the libraries this device can open. */
export interface LibraryIndex<L extends NamedLibrary = NamedLibrary> {
  libraries: L[]
  entries: Map<string, OpenEntry[]>
}

export interface IndexDeps<L> {
  libraries(): Promise<L[]>
  libraryKey(id: string): string | undefined
  api: EntryApi
}

/**
 * Loads the index: each library's entries decrypted here. `progress` gets the index as each
 * library arrives; a library that won't load (or whose key isn't here) is left out.
 */
export async function loadLibraryIndex<L extends NamedLibrary>(
  deps: IndexDeps<L>,
  progress?: (index: LibraryIndex<L>, pending: number) => void,
): Promise<LibraryIndex<L>> {
  const index: LibraryIndex<L> = { libraries: await deps.libraries(), entries: new Map() }
  const files = index.libraries.filter((l) => l.kind === 'files' && !l.locked)
  let pending = files.length
  progress?.(index, pending)
  await Promise.all(
    files.map(async (l) => {
      try {
        const key = deps.libraryKey(l.id)
        if (key) index.entries.set(l.id, [...(await openEntries(deps.api, key, l.id)).values()])
      } catch {
        // The others still search.
      } finally {
        progress?.(index, --pending)
      }
    }),
  )
  return index
}

/** Files matching across the whole index. */
export function searchIndex(index: LibraryIndex, query: string, all = false): FileHit[] {
  const out: FileHit[] = []
  for (const [id, rows] of index.entries) out.push(...searchEntries(id, rows, query, all))
  return out
}
